import { useEffect } from 'react';

/**
 * Coordinates lab-checkout session persistence:
 *  - one-time restore of saved wizard state on mount,
 *  - debounced-by-React save whenever wizard state changes,
 *  - re-running availability after a restore that landed on/after Step 3.
 *
 * State lives in feature hooks; this hook only wires save/restore to them.
 */
export default function useLabCheckoutPersistence({
  session,
  restoredRef,
  values,
  setters,
  collection,
}) {
  const { restore, save } = session;
  const {
    step, detailsMode, email, phone, dob, sex, zip, address,
    selectedMethod, selectedDrawSite, couponCode, appliedCoupon,
    aoeAnswers, consentHipaa, consentTerms, consentTele,
  } = values;

  // ── Restore once on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (restoredRef.current) return;
    const saved = restore();
    if (!saved) {
      restoredRef.current = true;
      return;
    }
    if (typeof saved.step === 'number') setters.setStep(Math.min(Math.max(saved.step, 1), 5));
    if (saved.detailsMode) setters.setDetailsMode(saved.detailsMode);
    if (saved.email) setters.setEmail(saved.email);
    if (saved.phone) setters.setPhone(saved.phone);
    if (saved.dob) setters.setDob(saved.dob);
    if (saved.sex) setters.setSex(saved.sex);
    if (saved.zip) setters.setZip(saved.zip);
    if (saved.address) setters.setAddress(prev => ({ ...prev, ...saved.address }));
    if (saved.selectedDrawSite) setters.setSelectedDrawSite(saved.selectedDrawSite);
    if (saved.couponCode) setters.setCouponCode(saved.couponCode);
    if (saved.appliedCoupon) setters.setAppliedCoupon(saved.appliedCoupon);
    if (saved.aoeAnswers) setters.setAoeAnswers(saved.aoeAnswers);
    setters.setConsentHipaa(Boolean(saved.consentHipaa));
    setters.setConsentTerms(Boolean(saved.consentTerms));
    setters.setConsentTele(Boolean(saved.consentTele));
    restoredRef.current = true;
  }, [restore]);

  // ── Persist on change ────────────────────────────────────────────────────
  useEffect(() => {
    if (!restoredRef.current) return;
    save({
      step, detailsMode, email, phone, dob, sex, zip, address,
      selectedMethodHint: selectedMethod
        ? { method: selectedMethod.method, assignment_id: selectedMethod.assignment_id || '' }
        : null,
      selectedDrawSite, couponCode, appliedCoupon, aoeAnswers,
      consentHipaa, consentTerms, consentTele,
    });
  }, [
    step, detailsMode, email, phone, dob, sex, zip, address,
    selectedMethod, selectedDrawSite, couponCode, appliedCoupon, aoeAnswers,
    consentHipaa, consentTerms, consentTele, save,
  ]);

  // ── Re-check availability after a restore into Step 3+ ───────────────────
  const { lab, availResult, availLoading, handleCheckAvailability } = collection;
  useEffect(() => {
    if (!lab || !restoredRef.current) return;
    const saved = restore();
    if (!saved?.zip || availResult || availLoading) return;
    if ((saved.step || 1) < 3) return;
    handleCheckAvailability(saved.zip, { methodHint: saved.selectedMethodHint || null });
  }, [lab, restore, availResult, availLoading]);
}
